import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { config, isMarketingThemePickerEnabled } from '../config/env';
import {
  DEFAULT_MARKETING_THEME,
  MARKETING_THEME_STORAGE_KEY,
  MARKETING_THEMES,
  MarketingTheme,
  MarketingThemeId,
  isMarketingThemeId,
} from '../config/marketingThemes';

interface MarketingThemeContextValue {
  themeId: MarketingThemeId;
  theme: MarketingTheme;
  themes: MarketingTheme[];
  pickerEnabled: boolean;
  setThemeId: (id: MarketingThemeId) => void;
}

const MarketingThemeContext = createContext<MarketingThemeContextValue | null>(null);

const configuredTheme: MarketingThemeId = config.app.marketingTheme || DEFAULT_MARKETING_THEME;

function readStoredTheme(): MarketingThemeId | null {
  try {
    const raw = localStorage.getItem(MARKETING_THEME_STORAGE_KEY);
    if (isMarketingThemeId(raw)) return raw;
  } catch {
    /* ignore */
  }
  return null;
}

function applyThemeToDocument(themeId: MarketingThemeId) {
  document.documentElement.setAttribute('data-marketing-theme', themeId);
}

function resolveInitialTheme(): MarketingThemeId {
  if (!isMarketingThemePickerEnabled) return configuredTheme;
  return readStoredTheme() || configuredTheme;
}

export const MarketingThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pickerEnabled = isMarketingThemePickerEnabled;

  const [themeId, setThemeIdState] = useState<MarketingThemeId>(() => {
    if (typeof window === 'undefined') return configuredTheme;
    const id = resolveInitialTheme();
    applyThemeToDocument(id);
    return id;
  });

  useEffect(() => {
    if (!pickerEnabled && themeId !== configuredTheme) {
      setThemeIdState(configuredTheme);
      return;
    }
    applyThemeToDocument(themeId);
    if (!pickerEnabled) return;
    try {
      localStorage.setItem(MARKETING_THEME_STORAGE_KEY, themeId);
    } catch {
      /* ignore */
    }
  }, [themeId, pickerEnabled]);

  const setThemeId = useCallback(
    (id: MarketingThemeId) => {
      if (!pickerEnabled) return;
      setThemeIdState(id);
    },
    [pickerEnabled]
  );

  const theme = useMemo(
    () => MARKETING_THEMES.find((t) => t.id === themeId) || MARKETING_THEMES[0],
    [themeId]
  );

  const value = useMemo(
    () => ({
      themeId,
      theme,
      themes: MARKETING_THEMES,
      pickerEnabled,
      setThemeId,
    }),
    [themeId, theme, pickerEnabled, setThemeId]
  );

  return (
    <MarketingThemeContext.Provider value={value}>{children}</MarketingThemeContext.Provider>
  );
};

export function useMarketingTheme(): MarketingThemeContextValue {
  const ctx = useContext(MarketingThemeContext);
  if (!ctx) {
    throw new Error('useMarketingTheme must be used within MarketingThemeProvider');
  }
  return ctx;
}
